import debounce from 'debounce';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { VERSION } from '@/constants';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import TranslationService from '@/locale/translation.service';
import { Gravatar } from '@/shared/gravatar/gravatar';
import { IUserCompany } from '@/shared/model/user-company.model';
import { IUserDetails } from '@/shared/model/user.model';
import { IUserShop } from '@/shared/model/user-shop.model';
import { Role } from '@/shared/model/enumerations/role.model';
// @ts-ignore: No custon type available for SVG
import SLM_LOGO from '../../../content/images/slm-logo.svg';
import HeaderSearchService from '@/core/dashboard/dasboard.service';
import UserNotificationService from '@/core/slm-navbar/user-notification.service';

enum IActiveEntityType {
  SHOP = 'SHOP',
  COMPANY = 'COMPANY',
  NULL = 'NULL',
}

@Component({
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    lgAndUp() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
  },
})
export default class SlmNavbar extends Vue {
  @Inject('headerSearchService') private headerSearchService: () => HeaderSearchService;
  public searchText: string = '';
  public menuOpen: boolean = false;
  public mobileMenuOpen: boolean = false;
  public searchResults = {
    orders: {},
    products: {},
    productTypes: {},
  };
  @Inject('loginService')
  private loginService: () => LoginService;

  @Inject('translationService') private translationService: () => TranslationService;
  private currentLanguage = this.$store.getters.currentLanguage;
  private languages: any = this.$store.getters.languages;

  @Inject('accountService') private accountService: () => AccountService;
  @Inject('userNotificationService') private userNotificationService: () => UserNotificationService;

  public version = VERSION ? 'v' + VERSION : '';
  public slmLogo = SLM_LOGO;
  private hasAnyAuthorityValue = false;
  private isAdmin: boolean = false;
  private gravatarImgUrl = '';
  public key = 1;
  public activeShop = -1;
  public activeCompany = -1;
  public activeEntity: any = {};
  public isAuthenticated = false;
  public userShops: IUserShop[] = [];
  public userCompanies: IUserCompany[] = [];
  public doLogout = true;
  public dialog = false;
  public positionX: number = 0;
  public positionY: number = 0;
  public width: number = 0;
  public headerNotifications = [];

  created() {
    this.translationService().refreshTranslation(this.currentLanguage);
  }

  public mounted(): void {
    if (this.$route.meta?.authorities) {
      this.doLogout = true;
    } else {
      this.doLogout = false;
    }
    this.initialize();
    this.setUserEntities();
    (this.$root as Vue).$on('retrieve-header-notification', () => {
      this.retrieveNotification({ shopId: this.$store.getters.activeEntity?.id });
    });
    this.retrieveNotification({ shopId: this.activeEntity?.id });
    setInterval(() => this.retrieveNotification({ shopId: this.activeEntity?.id }), 10000);
  }

  public get menu() {
    return {
      items: [
        { title: this.$t('global.menu.dashboard'), icon: 'mdi-view-dashboard-outline', route: '/', isVisible: true },
        { title: this.$t('global.menu.entities.orders'), icon: 'mdi-cart-outline', route: '/order', isVisible: true },
        {
          title: this.$t('global.menu.entities.products'),
          icon: 'mdi-shopping-outline',
          route: '/product',
          isVisible: this.hasShopRole(Role.MANAGER),
        },
      ],
      settings: [
        {
          title: this.$t('global.menu.settings'),
          action: 'mdi-view-grid-outline',
          active: false,
          isVisible: this.hasShopRole(Role.MANAGER),
          items: [
            { title: this.$t('global.menu.entities.company'), route: '/company', isVisible: this.hasCompanyRole(Role.MANAGER) },
            { title: this.$t('global.menu.entities.userAccess'), route: '/user-access', isVisible: this.hasCompanyRole(Role.MANAGER) },
            { title: this.$t('global.menu.entities.shop'), route: '/shop', isVisible: this.hasCompanyRole(Role.EMPLOYEE) },
            { title: this.$t('global.menu.entities.userAccess'), route: '/user-shop', isVisible: this.hasCompanyRole(Role.EMPLOYEE) },
            { title: this.$t('global.menu.entities.productType'), route: '/product-type', isVisible: true },
            {
              title: this.$t('global.menu.entities.shopApprovalRule'),
              icon: 'mdi-scale-balance',
              route: '/shop-approval-rule',
              isVisible: true,
            },
          ],
        },
      ],
      adminSettings: [
        {
          title: this.$t('global.menu.admin.main'),
          action: 'mdi-cog-outline',
          active: true,
          items: [
            {
              title: this.$t('global.menu.entities.defaultEmailTemplate'),
              icon: 'mdi-email-newsletter',
              route: '/admin/email-template',
              isVisible: true,
            },
            {
              title: this.$t('global.menu.admin.userManagement'),
              icon: 'mdi-account-group',
              route: '/admin/user-management',
              isVisible: true,
            },
            { title: this.$t('global.menu.admin.metrics'), icon: 'mdi-speedometer', route: '/admin/metrics', isVisible: true },
            { title: this.$t('global.menu.admin.health'), icon: 'mdi-heart-pulse', route: '/admin/health', isVisible: true },
            { title: this.$t('global.menu.admin.configuration'), icon: 'mdi-tune', route: '/admin/configuration', isVisible: true },
            { title: this.$t('global.menu.admin.logs'), icon: 'mdi-format-list-bulleted-square', route: '/admin/logs', isVisible: true },
            { title: this.$t('global.menu.admin.apidocs'), icon: 'mdi-file-document', route: '/admin/docs', isVisible: true },
          ],
        },
      ],
    };
  }

  public get settingsRoutes() {
    const routes = this.menu.settings[0].items.map(item => item.route);
    return routes;
  }

  public subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    return paths.some(path => {
      return this.$route.path.indexOf(path) === 0; // current path starts with this path string
    });
  }

  public changeLanguage(newLanguage: string): void {
    this.translationService().refreshTranslation(newLanguage);
  }

  public isActiveLanguage(key: string): boolean {
    return key === this.$store.getters.currentLanguage;
  }

  public logout(): void {
    this.clearStates();
    this.$forceUpdate();
    this.accountService().logout();
    this.dialog = false;
  }

  public clearStates(): void {
    this.activeShop = -1;
    this.activeCompany = -1;
    this.isAuthenticated = false;
    this.userShops = [];
    this.userCompanies = [];
    this.activeEntity = {};
    this.isAdmin = false;
  }

  public openLogin(): void {
    this.loginService().openLogin((<any>this).$root);
  }

  public get authenticated(): boolean {
    return this.$store.getters.authenticated;
  }

  public toggleDrawer(): void {
    return this.$store.commit('toggleDrawer');
  }
  public toggleRightDrawer(): void {
    return this.$store.commit('toggleRightDrawer');
  }

  public hasAnyAuthority(authorities: any): boolean {
    if (this.accountService().userAuthorities?.length) {
      this.hasAnyAuthorityValue = this.accountService().hasAnyAuthority(authorities);
    } else {
      this.accountService()
        .hasAnyAuthorityAndCheckAuth(authorities)
        .then(value => {
          this.hasAnyAuthorityValue = value;
        });
    }

    return this.hasAnyAuthorityValue;
  }

  @Watch('menuOpen')
  public updateMobileMenu() {
    if (!this.menuOpen) {
      this.mobileMenuOpen = false;
    }
  }

  @Watch('hasAnyAuthorityValue')
  @Watch('userCompanies')
  @Watch('isAdmin')
  public updateMenu() {
    this.key += 1;
  }

  public hasCompanyRole(companyRole: string): boolean {
    if (this.userCompanies.length && !this.isAdmin) {
      const activeShop = this.userShops.find(shop => shop.shop.id === this.$store.getters.activeEntity?.id);
      const activeCompanyId = activeShop.shop.company.id;
      return this.userCompanies.some(
        company => company.role === companyRole && (!activeCompanyId || company.company.id === activeCompanyId)
      );
    }
    return companyRole === Role.MANAGER;
  }

  public hasShopRole(userRole: string): boolean {
    if (this.userShops.length && !this.isAdmin) {
      const activeShopId = this.$store.getters.activeEntity?.id;
      return this.userShops.some(userShop => userShop.role === userRole && (!activeShopId || userShop.shop.id === activeShopId));
    }
    return this.isAdmin;
  }

  @Watch('authenticated')
  public setUserEntities() {
    if (!this.authenticated) return;

    const userRoles = ['ROLE_ADMIN'];
    if (!this.hasAnyAuthority(userRoles)) {
      if (this.$store.getters.activeEntity?.id) {
        const { index, type } = this.$store.getters.activeEntity;
        this.activeShop = type.toUpperCase() === 'SHOP' ? index : -1;
        this.activeCompany = type.toUpperCase() === 'COMPANY' ? index : -1;
        this.activeEntity = this.$store.getters.activeEntity || {};
      } else {
        if (this.$store.getters.shops.length > 0) {
          this.setActiveEntity(this.$store.getters.shops[0], IActiveEntityType.SHOP, 0);
        }
      }
      this.userShops = this.$store.getters.shops || [];
      this.userCompanies = this.$store.getters.companies || [];
    } else {
      this.isAdmin = true;
    }
  }

  public initialize(): void {
    const authorities = 'ROLE_USER';
    if (this.authenticated) {
      this.accountService().retrieveAccount(this.doLogout);
    }
    this.accountService()
      .hasAnyAuthorityAndCheckAuth(authorities)
      .then(value => {
        this.hasAnyAuthorityValue = value;
      });
  }
  @Watch('$route', { immediate: true, deep: true })
  onUrlChange(newVal: any) {
    this.menuOpen = false;
    if (this.$route.meta?.authorities) {
      this.doLogout = true;
    } else {
      this.doLogout = false;
    }
    this.initialize();
  }

  public get openAPIEnabled(): boolean {
    return this.$store.getters.activeProfiles.indexOf('api-docs') > -1;
  }

  public get inProduction(): boolean {
    return this.$store.getters.activeProfiles.indexOf('prod') > -1;
  }

  public setActiveEntity(entity: any, type: IActiveEntityType, index: number): void {
    const { id } = entity[type.toLowerCase()];
    const activeEntity = {
      index,
      id,
      name: entity.shop.name,
      role: entity.role,
      type,
      company: entity.shop.company,
    };

    this.activeEntity = activeEntity;
    this.$store.commit('setActiveEntity', activeEntity);
    (this.$root as Vue).$emit('active-shop');
    const allPaths = this.$router.options.routes.map(route => route.path);

    if (type === 'COMPANY') {
      this.activeCompany = index;
      this.activeShop = -1;
    } else {
      this.activeShop = index;
      this.activeCompany = -1;

      this.retrieveNotification({ shopId: this.activeEntity?.id }); // will retrieve header notification
      (this.$root as Vue).$emit('retrieve-notification'); // will retrieve notification page

      if (this.hasCompanyRole(Role.EMPLOYEE) && this.hasShopRole(Role.MANAGER)) {
        if (this.$route.fullPath.includes('company') || this.$route.fullPath.includes('shop')) {
          this.$router.replace('/shop');
        } else if (this.$route.fullPath.includes('user-access')) {
          this.$router.replace('/user-shop');
        }
      } else if (this.hasCompanyRole(Role.EMPLOYEE) && this.hasShopRole(Role.EMPLOYEE)) {
        if (allPaths.some(path => this.$route.fullPath.includes(path))) {
          if (this.$route.fullPath !== '/') {
            this.$router.replace('/');
          }
        }
      } else {
        if (this.$route.fullPath.includes('user-shop')) {
          this.$router.replace('/user-access');
        } else if (this.$route.fullPath.includes('shop')) {
          this.$router.replace('/company');
        }
      }
    }
  }

  public get user(): IUserDetails | any {
    if (this.$store.getters.account) {
      const { id, firstName, lastName, email, login } = this.$store.getters.account;
      const fullName = firstName + ' ' + lastName;
      const initials = this.userInitials(fullName);
      this.gravatarImgUrl = Gravatar.fetchImage(email);
      return {
        id,
        email,
        login,
        initials,
        fullName,
      };
    }
    return {};
  }

  userInitials(fullName: string): string {
    const fullNameArr = fullName.split(' ');
    const initials = fullNameArr.shift().charAt(0) + fullNameArr.pop().charAt(0);
    return initials.toUpperCase();
  }

  openMenu() {
    const element = document.querySelector('.search-wrapper');
    const client = element.getBoundingClientRect();
    this.positionX = client.x;
    this.positionY = client.y;
    this.width = client.width;

    if (this.searchText.trim() !== '') {
      this.menuOpen = true;

      debounce(() => {
        this.headerSearchService()
          .retrieve(this.searchText, this.hasAnyAuthorityValue ? null : this.$store.getters.activeShop?.id)
          .then(
            res => {
              this.searchResults = res.data;
            },
            err => {
              console.error(err);
            }
          );
      }, 300)();
    } else {
      this.menuOpen = false;
    }
  }

  clearSearch() {
    this.searchText = '';
    this.menuOpen = false;
  }

  displayResult(item, type) {
    if (type === 'orders') {
      return `Order-No. ${item.orderNo}, ${item.firstname} ${item.lastname} (${item.email})`;
    } else if (type === 'products') {
      return `${item.product} (${item.productType.saneName})`;
    } else {
      return `${item.identifier} (${item.saneName})`;
    }
  }

  focusPlaceholder(e) {
    e.target.placeholder = 'Search for Order, Product, Product Type';
  }

  blurPlaceholder(e) {
    e.target.placeholder = 'Search for...';
  }

  notificationCount(notifications) {
    const viewedNotification = notifications.filter(notification => !notification.viewed).length;
    return viewedNotification;
  }

  patchNotification(id: number) {
    this.userNotificationService()
      .patch(id)
      .then(() => {
        this.retrieveNotification({ shopId: this.activeEntity.id });
      })
      .catch(err => {
        console.error(err);
      });
  }

  retrieveNotification(param) {
    if (this.hasAnyAuthority('ROLE_ADMIN') || !this.authenticated) {
      return;
    }

    this.userNotificationService()
      .retrieve(param)
      .then(res => {
        this.headerNotifications = this.transformNotification(res.data);
      })
      .catch(err => {
        console.error(err);
      });
  }

  transformNotification(res) {
    const notif = res.map(item => {
      const { message, ...rest } = item;
      const parsed = JSON.parse(message);
      return {
        ...parsed,
        ...rest,
      };
    });
    return notif;
  }

  transformText(str) {
    if (typeof str === 'string') {
      return str.toLowerCase();
    }
    return str;
  }

  navigateTo(path) {
    if (path === this.$route.path) {
      return;
    }
    return this.$router.push(path);
  }
}
